import { FormEvent, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Check, Loader2, MessageSquareHeart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

type FeedbackState =
  | { kind: "loading" }
  | { kind: "form"; businessName: string; listingName?: string | null }
  | { kind: "submitting"; businessName: string; listingName?: string | null }
  | { kind: "success"; businessName: string; urgent: boolean }
  | { kind: "unavailable"; message: string };

type FeedbackResponse = {
  mode?: "feedback" | "submitted";
  business_name?: string;
  listing_name?: string | null;
  urgent?: boolean;
  error?: string;
};

const readFunctionError = async (error: unknown, fallback: string) => {
  try {
    const response = (error as { context?: Response }).context;
    if (response) {
      const body = await response.json() as FeedbackResponse;
      if (body.error) return body.error;
    }
  } catch {
    // Keep the plain-language fallback when the network response has no JSON.
  }
  return fallback;
};

const tokenFromFragment = () => {
  try {
    const token = decodeURIComponent(window.location.hash.slice(1));
    return /^[A-Za-z0-9_-]{40,200}$/.test(token) ? token : null;
  } catch {
    return null;
  }
};

export default function CustomerFeedback() {
  const [state, setState] = useState<FeedbackState>({ kind: "loading" });
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [token] = useState(tokenFromFragment);

  useEffect(() => {
    // Remove the capability from the visible address bar after React has read
    // it. The API request still receives it, but browser history does not.
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ kind: "unavailable", message: "This feedback link is invalid or incomplete." });
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke<FeedbackResponse>("customer-feedback", {
          body: { token, action: "peek" },
        });
        if (cancelled) return;
        if (error) {
          setState({
            kind: "unavailable",
            message: await readFunctionError(error, "This feedback link is no longer available."),
          });
          return;
        }
        if (data?.mode === "submitted") {
          setState({
            kind: "success",
            businessName: data.business_name || "the business",
            urgent: data.urgent === true,
          });
          return;
        }
        if (data?.mode === "feedback") {
          setState({
            kind: "form",
            businessName: data.business_name || "the business",
            listingName: data.listing_name,
          });
          return;
        }
        setState({ kind: "unavailable", message: data?.error || "This feedback link is no longer available." });
      } catch {
        if (!cancelled) {
          setState({ kind: "unavailable", message: "We could not check this link. Please try again shortly." });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || state.kind !== "form" || rating === null) return;
    const formState = state;
    setSubmissionError(null);
    setState({ kind: "submitting", businessName: state.businessName, listingName: state.listingName });
    try {
      const { data, error } = await supabase.functions.invoke<FeedbackResponse>("customer-feedback", {
        body: { token, rating, comment },
      });
      if (error) {
        setState(formState);
        setSubmissionError(await readFunctionError(error, "We could not save that yet. Please try again."));
        return;
      }
      setState({
        kind: "success",
        businessName: data?.business_name || formState.businessName,
        urgent: data?.urgent === true,
      });
    } catch {
      setState(formState);
      setSubmissionError("We could not save that yet. Please try again.");
    }
  };

  const businessName = state.kind === "form" || state.kind === "submitting" || state.kind === "success"
    ? state.businessName
    : "Private feedback";

  return (
    <div className="min-h-screen bg-background px-4 py-10 flex items-center justify-center">
      <Helmet>
        <title>Private feedback</title>
        <meta name="robots" content="noindex,nofollow,noarchive" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <main className="w-full max-w-xl rounded-2xl border bg-background p-6 shadow-sm sm:p-10">
        <p className="mb-7 text-sm text-muted-foreground">{businessName}</p>

        {state.kind === "loading" && (
          <div className="py-12 text-center">
            <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Checking your private feedback link…</p>
          </div>
        )}

        {(state.kind === "form" || state.kind === "submitting") && (
          <form onSubmit={submit}>
            <MessageSquareHeart className="mb-4 h-9 w-9 text-primary" />
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">Private feedback</p>
            <h1 className="mb-3 text-3xl font-semibold tracking-tight">How was your experience?</h1>
            <p className="mb-7 leading-7 text-muted-foreground">
              Your answer goes privately to {state.businessName}
              {state.listingName ? ` about ${state.listingName}` : ""}. If something was not right, a person can follow up.
            </p>

            <fieldset disabled={state.kind === "submitting"}>
              <legend className="mb-3 font-medium">Choose a rating</legend>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-label={`${value} out of 5`}
                    aria-pressed={rating === value}
                    onClick={() => setRating(value)}
                    className={`min-h-14 rounded-xl border text-base font-bold transition-colors ${
                      rating === value
                        ? "border-primary bg-primary/10 text-primary ring-2 ring-primary/20"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                <span>Very poor</span><span>Excellent</span>
              </div>

              <label htmlFor="feedback-comment" className="mt-7 block font-medium">What should the team know?</label>
              <p className="mb-2 mt-1 text-xs text-muted-foreground">Optional · please do not include payment card details.</p>
              <textarea
                id="feedback-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value.slice(0, 2000))}
                maxLength={2000}
                rows={5}
                className="w-full resize-y rounded-xl border bg-background p-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Tell the team what went well or what could be better…"
              />
              {submissionError && (
                <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {submissionError}
                </p>
              )}
              <Button type="submit" size="lg" className="mt-5 w-full" disabled={rating === null || state.kind === "submitting"}>
                {state.kind === "submitting" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Send private feedback"}
              </Button>
            </fieldset>
            <p className="mt-4 text-xs leading-5 text-muted-foreground">
              Your response is not posted publicly or used to pressure you into leaving a review.
            </p>
          </form>
        )}

        {state.kind === "success" && (
          <div className="py-4">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-700" />
            </div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">Response received</p>
            <h1 className="mb-3 text-3xl font-semibold tracking-tight">Thank you for telling us</h1>
            <p className="leading-7 text-muted-foreground">Your response is private and has been shared with {state.businessName}.</p>
            {state.urgent && (
              <p className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                If you may need urgent medical or safety help, contact your local emergency service now. This form is not an emergency service.
              </p>
            )}
            <p className="mt-5 text-xs leading-5 text-muted-foreground">Dibs does not post your response publicly or automatically offer compensation.</p>
          </div>
        )}

        {state.kind === "unavailable" && (
          <div className="py-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">Private feedback</p>
            <h1 className="mb-3 text-3xl font-semibold tracking-tight">This link is not available</h1>
            <p className="leading-7 text-muted-foreground">{state.message}</p>
            <p className="mt-5 text-sm text-muted-foreground">Please contact the business directly if you still want to share something.</p>
          </div>
        )}
      </main>
    </div>
  );
}
